// SeasonsPage.js — /seasons: every Premier League season, newest first: the
// season's goals a game, its champions (or current leaders) with their
// W–D–L record and points (after deductions), and what they won it by
// ("7 pts over Man City", "Goal difference over Man United"), each linking
// to the season's page; then the titles each club has won. Computed in the
// browser from ?matches=1 (league.js), like the all-time table.

import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Skeleton } from "@mui/material";
import SEO from "./components/SEO";
import AdSlot from "./components/AdSlot";
import { CtaRow, H2, P, PageHeader, ProsePage } from "./components/Prose";
import { BRAND } from "./theme";
import { useSeededQuery } from "./components/scorelines/data";
import { MATCHES_QUERY, parseMatches, tableFor } from "./components/clubs/league";
import { clubInfo, clubPath } from "./components/clubs/data";
import { seasonPath, seasonSummaries } from "./components/clubs/seasons";

const SITE_URL = "https://premierleaguescorigami.com";

export default function SeasonsPage() {
  const resp = useSeededQuery(MATCHES_QUERY);
  const data = useMemo(() => (resp.data ? parseMatches(resp.data) : null), [resp.data]);
  const seasons = useMemo(() => (data ? seasonSummaries(data, tableFor) : null), [data]);

  return (
    <ProsePage wide>
      <SEO
        title="Premier League champions: every season since 1992/93"
        description="Every Premier League champion since 1992/93: their record, points and winning margin, and a page for each season with its final table, title race and records."
        path="/seasons"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Seasons", item: `${SITE_URL}/seasons` },
          ],
        }}
      />
      {resp.error ? (
        <>
          <PageHeader eyebrow="Seasons" title="Every Premier League season" />
          <P>The seasons couldn't be loaded right now. Please try again shortly.</P>
        </>
      ) : !seasons ? (
        <>
          <PageHeader eyebrow="Seasons" title="Every Premier League season" />
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} height={44} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />
          ))}
        </>
      ) : (
        <Seasons seasons={seasons} />
      )}
      <CtaRow
        links={[
          { to: "/all-time-table", label: "All-time table", primary: true },
          { to: "/race", label: "Race chart" },
          { to: "/clubs", label: "Every club" },
        ]}
      />
    </ProsePage>
  );
}

// What the champions won it by (or the leaders lead by), naming the runners-up.
function wonBy(s) {
  const [first, second] = s.table;
  if (!second) return null;
  const runner = (
    <RouterLink to={clubPath(second.team)}>{clubInfo(second.team).short}</RouterLink>
  );
  const gap = first.pts - second.pts;
  if (gap > 0)
    return (
      <>
        {gap} pt{gap === 1 ? "" : "s"} {s.complete ? "over" : "clear of"} {runner}
      </>
    );
  return (
    <>
      {first.gd > second.gd ? "Goal difference" : "Goals scored"} over {runner}
    </>
  );
}

function Seasons({ seasons }) {
  const complete = seasons.filter((s) => s.complete);
  const current = seasons.find((s) => !s.complete);
  // Titles per club, most first (ties: the most recent title first).
  const titles = new Map();
  for (const s of [...complete].reverse()) {
    const team = s.table[0].team;
    titles.set(team, [...(titles.get(team) || []), s]);
  }
  const byTitles = [...titles].sort((a, b) => b[1].length - a[1].length || b[1][b[1].length - 1].season - a[1][a[1].length - 1].season);
  const [topTeam, topSeasons] = byTitles[0];
  const name = (team) => clubInfo(team).name;
  const lede =
    `${complete.length} completed seasons since 1992/93, won by ${titles.size} different clubs. ` +
    `${name(topTeam)} have the most titles, ${topSeasons.length}.` +
    (current ? ` In ${current.label}, ${name(current.table[0].team)} lead after ${current.matches} matches.` : "");

  return (
    <>
      <Box sx={{ maxWidth: 820 }}>
        <PageHeader eyebrow="Seasons" title="Every Premier League season" lede={lede} />
        <P>Pick a season for its final table, title race, scoreline grid, biggest wins and first-ever results.</P>
      </Box>

      <Box sx={{ overflowX: "auto", mb: 3 }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            minWidth: { xs: 0, sm: 720 },
            borderCollapse: "collapse",
            fontFeatureSettings: '"tnum"',
            "& th": { textAlign: "left", color: BRAND.textSecondary, fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", py: 1, px: 1, borderBottom: "1px solid rgba(255,255,255,0.12)" },
            "& td": { textAlign: "left", py: 1, px: 1, borderBottom: "1px solid rgba(255,255,255,0.06)", color: "#d7dae3", fontSize: 14.5, whiteSpace: "nowrap" },
            "& th.num, & td.num": { textAlign: "right" },
            "& td.season a": { color: "#fff", fontWeight: 800, textDecoration: "none" },
            "& td a": { color: "#fff", textDecoration: "none" },
            "& td a:hover": { color: BRAND.cyan },
            "& td.pts": { color: "#fff", fontWeight: 800 },
            "& .by": { color: BRAND.textSecondary, whiteSpace: "normal" },
            "& .by a": { color: "#d7dae3", fontWeight: 700 },
            // Phones keep season, champions (with the margin beneath) and points;
            // the season average is on every season page.
            "& .gpg, & .wide": { display: { xs: "none", sm: "table-cell" } },
          }}
        >
          <thead>
            <tr>
              <th>Season</th>
              <th className="num gpg" title="The average across every match in the season">
                Season goals a game
              </th>
              <th>Champions</th>
              <th className="num wide">W–D–L</th>
              <th className="num">Pts</th>
              <th className="wide">Won by</th>
            </tr>
          </thead>
          <tbody>
            {seasons.map((s) => {
              const [first] = s.table;
              return (
                <tr key={s.season}>
                  <td className="season">
                    <RouterLink to={seasonPath(s.season)}>{s.label}</RouterLink>
                  </td>
                  <td className="num gpg">{(s.goals / s.matches).toFixed(2)}</td>
                  <td>
                    <Club team={first.team} image={first.image} />
                    {!s.complete && (
                      <Box component="span" sx={{ color: BRAND.cyan, fontSize: 12, fontWeight: 800, ml: 1 }}>
                        LEADING
                      </Box>
                    )}
                    {/* Phones: the margin goes under the name instead of its own column. */}
                    <Box className="by" sx={{ display: { xs: "block", sm: "none" }, fontSize: 12.5, mt: 0.25, pl: "30px" }}>
                      {wonBy(s)}
                    </Box>
                  </td>
                  <td className="num wide">
                    {first.won}–{first.drawn}–{first.lost}
                  </td>
                  <td className="num pts">
                    {first.pts}
                    {first.deducted > 0 ? "*" : ""}
                  </td>
                  <td className="by wide">{wonBy(s)}</td>
                </tr>
              );
            })}
          </tbody>
        </Box>
      </Box>
      <P>
        {current &&
          `${current.label} is in progress (${current.matches} of ${current.table.length * (current.table.length - 1)} matches played), so its row shows the current leaders. `}
        Season goals a game is the average across every match that season.
        {seasons.some((s) => s.table[0].deducted > 0) && " Points are after deductions (*)."}
      </P>

      <AdSlot position="records" />

      <H2 id="titles">Titles by club</H2>
      <Box sx={{ overflowX: "auto", mb: 2 }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            "& td": { py: 1.1, px: 1, borderBottom: "1px solid rgba(255,255,255,0.06)", color: "#d7dae3", fontSize: 14.5, verticalAlign: "top" },
            "& td:first-of-type": { pl: 0, whiteSpace: "nowrap" },
            "& td.n": { color: "#fff", fontWeight: 900, fontSize: 18, textAlign: "right", width: 40 },
            "& td a": { color: "#d7dae3", textDecoration: "none" },
            "& td a:hover": { color: BRAND.cyan },
            "& td.club a": { color: "#fff", fontWeight: 700 },
          }}
        >
          <tbody>
            {byTitles.map(([team, won]) => (
              <tr key={team}>
                <td className="club">
                  <Club team={team} image={won[0].table[0].image} />
                </td>
                <td className="n">{won.length}</td>
                <td>
                  {won.map((s, i) => (
                    <React.Fragment key={s.season}>
                      {i > 0 && ", "}
                      <RouterLink to={seasonPath(s.season)}>{s.label}</RouterLink>
                    </React.Fragment>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
    </>
  );
}

function Club({ team, image }) {
  return (
    <Box component={RouterLink} to={clubPath(team)} sx={{ display: "inline-flex", alignItems: "center", gap: 1, verticalAlign: "middle" }}>
      {image && <Box component="img" src={image} alt="" loading="lazy" sx={{ width: 22, height: 22, objectFit: "contain", flexShrink: 0 }} />}
      {/* Short names on phones keep the table inside the screen. */}
      <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
        {clubInfo(team).name}
      </Box>
      <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
        {clubInfo(team).short}
      </Box>
    </Box>
  );
}
